import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { pollPayPayPaymentStatus } from "works/higurashi/utils/api";
import { Typography, Box } from "@mui/material";
import AppLayout from "works/higurashi/components/AppLayout";
import StyledButton from "default/components/StyledButton";
import Loading from "default/components/Loading";
import { COLORS } from "constants/colors";
import { useUrlParams } from "works/higurashi/hooks/useUrlParams";

// Payment status constants
const PAYMENT_STATUS = {
  COMPLETED: "COMPLETED",
  CREATED: "CREATED",
  ERROR: "ERROR",
};

// Helper function to get status content
const getStatusContent = (status, navigate, successPath, failPath) => {
  const navigateToSuccess = () => navigate(successPath);
  const navigateToFail = () => navigate(failPath);

  switch (status) {
    case PAYMENT_STATUS.CREATED:
      return {
        message: "購入が完了しておりません。\n再度購入してください。",
        buttonLabel: "購入画面へ",
        onClick: navigateToFail,
      };
    case PAYMENT_STATUS.COMPLETED:
      return {
        message: "決済が完了しました。",
        buttonLabel: "完了",
        onClick: navigateToSuccess,
      };
    case PAYMENT_STATUS.ERROR:
    default:
      return {
        message: "エラーが発生しました。",
        buttonLabel: "購入画面へ",
        onClick: navigateToFail,
      };
  }
};

// Custom hook for payment status management
const usePaymentStatus = (merchantPaymentId) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkPaymentStatus = async () => {
    if (!merchantPaymentId) {
      setStatus(PAYMENT_STATUS.ERROR);
      setLoading(false);
      return;
    }

    try {
      const { status: paymentStatus } = await pollPayPayPaymentStatus(
        merchantPaymentId
      );

      if (paymentStatus === PAYMENT_STATUS.COMPLETED) {
        setStatus(PAYMENT_STATUS.COMPLETED);
      } else if (paymentStatus === PAYMENT_STATUS.CREATED) {
        setStatus(PAYMENT_STATUS.CREATED);
      } else {
        setStatus(PAYMENT_STATUS.ERROR);
      }
    } catch (error) {
      setStatus(PAYMENT_STATUS.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkPaymentStatus();
  }, [merchantPaymentId]);

  return { status, loading };
};

// Component for status content
const StatusContent = ({ content }) => (
  <Box
    sx={{
      width: "88%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: COLORS.BACKGROUND_WHITE,
      borderRadius: "5px",
      padding: 4,
      gap: 4,
    }}
  >
    <Typography
      variant="h6"
      sx={(theme) => ({
        mt: 1,
        whiteSpace: "pre-line",
        textAlign: "center",
        color: COLORS.TEXT_BLACK,
      })}
    >
      {content.message}
    </Typography>

    <StyledButton
      variant="contained"
      sx={{
        width: "80%",
        color: COLORS.TEXT_BLACK,
        fontWeight: "bold",
        border: `2px solid ${COLORS.TEXT_BLACK}`,
      }}
      onClick={content.onClick}
    >
      {content.buttonLabel}
    </StyledButton>
  </Box>
);

function PayPayCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const { work, accountKey } = useUrlParams();

  console.log(accountKey);

  const queryParams = new URLSearchParams(location.search);
  const merchantPaymentId = queryParams.get("merchant_payment_id");

  const successPath = `/${work}/how-to-use?account=${accountKey}`;
  const failPath = `/${work}/tickets?account=${accountKey}`;

  const { status, loading } = usePaymentStatus(merchantPaymentId);

  if (loading) {
    return (
      <AppLayout page="PayPayCheck">
        <Loading />
      </AppLayout>
    );
  }

  const content = getStatusContent(status, navigate, successPath, failPath);

  return (
    <AppLayout page="PayPayCheck">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "100vh",
        }}
      >
        <StatusContent content={content} />
      </Box>
    </AppLayout>
  );
}

export default PayPayCheck;
